/**
 * The one normalisation between `bundle.json` as the baker wrote it and the `Bundle` the player
 * types against (docs/SKEL-STORY.md §3.5). The player never zod-parses at runtime, so the two
 * wire variants the contract allows are folded here, once, at the mount:
 *
 * - `cast` / `props` as an array of entries (each with `handle`) → keyed by handle;
 * - an asset's location as `src` → `url`;
 * - a scene's `transition` / `ending` (SKEL-STORY "Scene transitions") made safe to play: `dur`
 *   in `[0, 4]`, `lead` and `hold` in `[0, dur]`, an unknown `kind` a `fade`, and an `ending`
 *   anywhere but on the last scene dropped.
 *
 * Pure, shallow (entries are shared, not cloned) and idempotent: a bundle that is already in
 * shape comes back equal, so every entry point may call it.
 */
import { keyByHandle } from '@/schema/story'
import type { Bundle } from '@/schema/story'

import { normalizeTransition } from './transition'

/** A scene with its transition fields made playable; the same object when nothing had to change. */
function normalizeScene(scene: unknown, last: boolean): unknown {
  if (!scene || typeof scene !== 'object') return scene
  const s = scene as Record<string, unknown>
  const transition = normalizeTransition(s.transition)
  const ending = last ? normalizeTransition(s.ending) : undefined
  if (transition === s.transition && ending === s.ending) return scene
  const out: Record<string, unknown> = { ...s }
  if (transition) out.transition = transition
  else delete out.transition
  if (ending) out.ending = ending
  else delete out.ending
  return out
}

export function normalizeBundle(raw: unknown): Bundle {
  if (!raw || typeof raw !== 'object') return raw as Bundle
  const b = raw as Record<string, unknown>
  const assets: Record<string, unknown> = {}
  const rawAssets = b.assets && typeof b.assets === 'object' ? (b.assets as Record<string, unknown>) : {}
  for (const [id, a] of Object.entries(rawAssets)) {
    if (a && typeof a === 'object') {
      const e = a as { url?: unknown; src?: unknown }
      assets[id] = typeof e.url === 'string' && e.url.length > 0 ? a : typeof e.src === 'string' ? { ...e, url: e.src } : a
    } else {
      assets[id] = a
    }
  }
  const scenes = Array.isArray(b.scenes) ? b.scenes.map((s, i, all) => normalizeScene(s, i === all.length - 1)) : b.scenes
  return {
    ...b,
    assets,
    cast: (keyByHandle(b.cast) as Record<string, unknown> | undefined) ?? {},
    props: (keyByHandle(b.props) as Record<string, unknown> | undefined) ?? {},
    scenes,
  } as unknown as Bundle
}
